/**
 * SageShell — the SAGE desktop platform frame.
 *
 * This is the design Evan articulated (CSV journal) and the S25 companion app
 * embodies — NOT a sidebar admin dashboard:
 *   - Horizontal carousel of full-bleed panels (translateX by -index*100%)
 *   - Vertical stack: Personal (swipe up) / primary carousel / Media (swipe down)
 *   - OMNIPRESENT floating chat bar on every surface (context pill, suggestion
 *     chips, modes row, input, persona + model pills)
 *   - Daily Brief is the opening backdrop (center panel)
 *   - Warm-stone SAGE Design System palette (NOT the companion's cyan)
 *
 * UNIVERSAL TRACKPAD CHAT BAR (full parity with the S25 PerplexedShell):
 *   - The CHAT BAR is a trackpad. Swiping / wheeling / dragging ON the bar
 *     navigates panels (left/right = carousel, up/down = Personal/Media stack).
 *     Reserved for app-level control. Swiping on panel CONTENT scrolls content.
 *   - Two-axis single-finger touch swipe on the bar (ThinkPad touchscreen + S25).
 *   - Trackpad two-finger scroll on the bar (laptop) → wheel deltas → nav.
 *   - Mouse click-drag on the bar (non-interactive zones) → nav.
 *   - Pinch-in (anywhere) → grid overview of all panels.
 *   - Two-finger swipe-down (anywhere) → breadcrumb strip of recent surfaces.
 *   - 4-edge position indicator on the chat card shows where you are.
 *   - Desktop affordances kept additively: top panel rail, ◄ ► arrow keys.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import { Plus, Mic, Send, ChevronDown, X, Brain, Code as CodeIcon, PenLine, Telescope } from "lucide-react";

export interface SageShellHandle {
  /** Navigate the carousel to a panel by id (resets vertical to primary). */
  goToId: (id: string) => void;
}

export interface Panel {
  id: string;
  title: string;
  icon?: ReactNode;
  /** Biome/accent color CSS var for the rail + chat-bar hint (e.g. "--biome-finance"). */
  accentVar?: string;
  content: ReactNode;
}

export interface VerticalPanel {
  id: string;
  title: string;
  accentVar?: string;
  content: ReactNode;
}

export type GenMode = "code" | "write" | "research";

export interface ContextSource {
  label: string;
  onDismiss?: () => void;
}

interface SageShellProps {
  panels: Panel[];
  initialPanelIndex?: number;
  panelAbove?: VerticalPanel;
  panelBelow?: VerticalPanel;
  contextSource?: ContextSource;
  onClearContext?: () => void;
  onSend?: (text: string) => void | Promise<void>;
  disabled?: boolean;
  suggestions?: string[];
  onSuggestionClick?: (text: string) => void;
  activePersona?: { shortName: string; color: string };
  onPersonaClick?: () => void;
  thinkingEnabled?: boolean;
  onThinkingToggle?: () => void;
  modelBadge?: string;
  onModelClick?: () => void;
  activeMode?: GenMode;
  onModeToggle?: (m: GenMode) => void;
  onMicClick?: () => void;
  onActivePanelChange?: (index: number, id: string) => void;
}

const SWIPE_MIN = 50;     // px — single-finger swipe distance to flip
const WHEEL_TH = 64;      // accumulated wheel delta to flip
const WHEEL_COOLDOWN = 420; // ms lockout after a wheel-triggered flip (eats momentum)

const SageShell = forwardRef<SageShellHandle, SageShellProps>(function SageShell({
  panels,
  initialPanelIndex,
  panelAbove,
  panelBelow,
  contextSource,
  onClearContext,
  onSend,
  disabled,
  suggestions = [],
  onSuggestionClick,
  activePersona,
  onPersonaClick,
  thinkingEnabled,
  onThinkingToggle,
  modelBadge,
  onModelClick,
  activeMode,
  onModeToggle,
  onMicClick,
  onActivePanelChange,
}, ref) {
  const defaultIdx = initialPanelIndex ?? Math.floor(panels.length / 2);
  const [active, setActive] = useState(defaultIdx);
  const [vertical, setVertical] = useState(0); // -1 above, 0 primary, +1 below

  // Overview affordances (parity with PerplexedShell CB.4).
  const [showGrid, setShowGrid] = useState(false);
  const [showCrumbs, setShowCrumbs] = useState(false);
  const [crumbs, setCrumbs] = useState<number[]>([defaultIdx]);

  useImperativeHandle(ref, () => ({
    goToId: (id: string) => {
      const idx = panels.findIndex((p) => p.id === id);
      if (idx >= 0) { setVertical(0); setActive(idx); }
    },
  }), [panels]);
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const go = useCallback(
    (next: number) => setActive((i) => {
      const t = typeof next === "number" ? next : i;
      return Math.max(0, Math.min(panels.length - 1, t));
    }),
    [panels.length],
  );
  const navH = useCallback((dir: number) => go(active + dir), [go, active]);
  const navV = useCallback((dir: number) => setVertical((v) => {
    const nv = v + dir;
    if (nv < 0 && !panelAbove) return v;
    if (nv > 0 && !panelBelow) return v;
    return Math.max(-1, Math.min(1, nv));
  }), [panelAbove, panelBelow]);

  useEffect(() => {
    onActivePanelChange?.(active, panels[active]?.id ?? "");
    setCrumbs((c) => (c[c.length - 1] === active ? c : [...c, active].slice(-8)));
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard nav (desktop) — arrows when not typing in the chat box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setShowGrid(false); setShowCrumbs(false); return; }
      if (focused) return;
      // 'g' (or Cmd/Ctrl+K) zooms out to the grid — the desktop equivalent of
      // pinch-in, since there is no rail. It's the "see the whole map" view.
      if (e.key === "g" || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) { e.preventDefault(); setShowGrid((v) => !v); return; }
      if (e.key === "ArrowRight") navH(1);
      else if (e.key === "ArrowLeft") navH(-1);
      else if (e.key === "ArrowUp" && panelAbove) navV(-1);
      else if (e.key === "ArrowDown" && panelBelow) navV(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focused, navH, navV, panelAbove, panelBelow]);

  // ── GLOBAL multi-touch (shell root): pinch → grid, 2-finger-down → breadcrumb.
  //    Single-finger touches are ignored here so panel content scrolls natively.
  const twoStart = useRef<{ dist: number; avgX: number; avgY: number } | null>(null);
  const twoLast = useRef<{ dist: number; avgX: number; avgY: number } | null>(null);
  const snap2 = (t: React.TouchList) => {
    const a = t[0], b = t[1];
    return { dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), avgX: (a.clientX + b.clientX) / 2, avgY: (a.clientY + b.clientY) / 2 };
  };
  const rootTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) { twoStart.current = snap2(e.touches); twoLast.current = twoStart.current; }
    else { twoStart.current = null; twoLast.current = null; }
  };
  const rootTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && twoStart.current) twoLast.current = snap2(e.touches);
  };
  const rootTouchEnd = () => {
    if (twoStart.current && twoLast.current) {
      const s = twoStart.current, l = twoLast.current;
      const ratio = l.dist / Math.max(s.dist, 1);
      const dy = l.avgY - s.avgY, dx = l.avgX - s.avgX;
      if (ratio < 0.7) setShowGrid(true);
      else if (ratio > 1.4 && showGrid) setShowGrid(false);
      else if (dy > 50 && Math.abs(dy) > Math.abs(dx) && Math.abs(ratio - 1) < 0.2) setShowCrumbs(true);
      else if (dy < -50 && showCrumbs) setShowCrumbs(false);
    }
    twoStart.current = null; twoLast.current = null;
  };

  // ── CHAT BAR trackpad: single-finger touch swipe + mouse drag → two-axis nav.
  const barStart = useRef<{ x: number; y: number } | null>(null);
  const barDragging = useRef(false);
  const resolveSwipe = useCallback((dx: number, dy: number) => {
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > SWIPE_MIN) navH(1);
      else if (dx < -SWIPE_MIN) navH(-1);
    } else {
      if (dy > SWIPE_MIN) navV(1);
      else if (dy < -SWIPE_MIN) navV(-1);
    }
  }, [navH, navV]);

  const barTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) { barStart.current = null; return; }
    barStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const barTouchEnd = (e: React.TouchEvent) => {
    if (!barStart.current) return;
    const t = e.changedTouches[0];
    resolveSwipe(barStart.current.x - t.clientX, barStart.current.y - t.clientY);
    barStart.current = null;
  };
  // Mouse drag on the bar — but let clicks on buttons/textarea behave normally.
  const isInteractive = (el: EventTarget | null) =>
    el instanceof Element && !!el.closest("button, textarea, input, a, [data-no-drag]");
  const barMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || isInteractive(e.target)) return;
    barStart.current = { x: e.clientX, y: e.clientY };
    barDragging.current = true;
  };
  const barMouseUp = (e: React.MouseEvent) => {
    if (!barDragging.current || !barStart.current) { barDragging.current = false; return; }
    resolveSwipe(barStart.current.x - e.clientX, barStart.current.y - e.clientY);
    barStart.current = null;
    barDragging.current = false;
  };

  // Trackpad two-finger scroll on the bar → wheel deltas → nav. Native non-passive
  // listener so we can preventDefault (React onWheel is passive in some builds).
  const barRef = useRef<HTMLDivElement>(null);
  const wheelAccX = useRef(0);
  const wheelAccY = useRef(0);
  const wheelLock = useRef(0);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const now = performance.now();
      if (now < wheelLock.current) return;
      const dx = e.shiftKey ? e.deltaY : e.deltaX;
      const dy = e.shiftKey ? 0 : e.deltaY;
      wheelAccX.current += dx;
      wheelAccY.current += dy;
      if (Math.abs(wheelAccX.current) >= Math.abs(wheelAccY.current)) {
        if (Math.abs(wheelAccX.current) > WHEEL_TH) {
          navH(wheelAccX.current > 0 ? 1 : -1);
          wheelAccX.current = 0; wheelAccY.current = 0; wheelLock.current = now + WHEEL_COOLDOWN;
        }
      } else if (Math.abs(wheelAccY.current) > WHEEL_TH) {
        navV(wheelAccY.current > 0 ? 1 : -1);
        wheelAccX.current = 0; wheelAccY.current = 0; wheelLock.current = now + WHEEL_COOLDOWN;
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [navH, navV]);

  const handleSend = useCallback(async () => {
    if (disabled) return;
    if (!input.trim() && !contextSource) return;
    await onSend?.(input);
    setInput("");
  }, [input, contextSource, disabled, onSend]);

  const hasText = input.trim().length > 0;
  const activeTitle = panels[active]?.title ?? "";
  const showSuggestions = suggestions.length > 0 && !focused && !contextSource && !hasText;

  return (
    <div
      className="relative w-full h-full bg-bg font-sans overflow-hidden flex flex-col"
      data-testid="sage-root"
      data-active-panel={active}
      data-active-id={panels[active]?.id ?? ""}
      data-vertical={vertical}
      data-grid-open={showGrid ? "1" : "0"}
      data-crumbs-open={showCrumbs ? "1" : "0"}
      onTouchStart={rootTouchStart}
      onTouchMove={rootTouchMove}
      onTouchEnd={rootTouchEnd}
    >
      {/* NO nav rail / tab bar / arrows — swipe-only per the Perplexed-is-Fun
          framework ("Remove that button bar… this is an app where you swipe
          between panels"). The floating chat bar is the only chrome; the frame
          slides across the map. Pinch-in (or the 'g' key) zooms out to the grid
          overview = the whole map, which is the discoverability surface. */}

      {/* CANVAS — vertical stack of three layers; primary layer is the horizontal carousel. */}
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0" style={{ transform: `translateY(${-vertical * 100}%)`, transition: "transform 380ms cubic-bezier(0.2,0.8,0.2,1)" }}>
          {panelAbove && (
            <div className="absolute left-0 right-0 overflow-auto" style={{ top: "-100%", height: "100%" }}>
              {vertical === -1 && panelAbove.content}
            </div>
          )}
          <div className="absolute left-0 right-0 overflow-hidden" style={{ top: 0, height: "100%" }}>
            <div className="flex h-full w-full" style={{ transform: `translateX(${-active * 100}%)`, transition: "transform 350ms cubic-bezier(0.2,0.8,0.2,1)" }}>
              {panels.map((p) => (
                <div key={p.id} className="min-w-full h-full overflow-auto relative">
                  {p.content}
                </div>
              ))}
            </div>
          </div>
          {panelBelow && (
            <div className="absolute left-0 right-0 overflow-auto" style={{ top: "100%", height: "100%" }}>
              {vertical === 1 && panelBelow.content}
            </div>
          )}
        </div>
      </div>

      {/* GRID OVERVIEW (pinch-in opens; tap a tile to jump). */}
      {showGrid && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col" onClick={() => setShowGrid(false)}>
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="text-caption uppercase tracking-wider text-text-muted font-bold">All Surfaces · pinch out or tap to close</span>
            <button onClick={() => setShowGrid(false)} className="text-text-muted hover:text-text" aria-label="Close grid overview"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {panelAbove && (
                <GridTile title={panelAbove.title} sub="swipe up · personal" accentVar={panelAbove.accentVar} active={vertical === -1}
                  onClick={() => { setVertical(-1); setShowGrid(false); }} />
              )}
              {panels.map((p, i) => (
                <GridTile key={p.id} title={p.title} sub={`panel ${i + 1}`} icon={p.icon} accentVar={p.accentVar} active={vertical === 0 && i === active}
                  onClick={() => { setVertical(0); go(i); setShowGrid(false); }} />
              ))}
              {panelBelow && (
                <GridTile title={panelBelow.title} sub="swipe down · media" accentVar={panelBelow.accentVar} active={vertical === 1}
                  onClick={() => { setVertical(1); setShowGrid(false); }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* BREADCRUMB STRIP (two-finger swipe-down opens). */}
      {showCrumbs && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-surface-raised/95 backdrop-blur-sm border-b border-primary/30 px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-caption uppercase tracking-wider text-primary font-bold">Recent</span>
            <div className="flex-1 flex gap-1.5 overflow-x-auto scrollbar-hide">
              {crumbs.length === 0 && <span className="text-body-sm text-text-dim">No history yet</span>}
              {crumbs.map((idx, pos) => {
                const p = panels[idx];
                if (!p) return null;
                const cur = idx === active && vertical === 0;
                return (
                  <button key={`${idx}-${pos}`} onClick={() => { setVertical(0); go(idx); setShowCrumbs(false); }}
                    className={`shrink-0 px-2 py-1 rounded-full text-body-sm font-medium transition-colors border ${cur ? "bg-primary-muted text-primary border-primary/40" : "bg-surface text-text-muted border-border hover:text-text"}`}>
                    {p.title}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setShowCrumbs(false)} className="shrink-0 text-text-muted hover:text-text p-1" aria-label="Hide breadcrumb"><X size={14} /></button>
          </div>
        </div>
      )}

      {/* ACTION SHEET */}
      {showActions && (
        <div className="fixed inset-0 z-50" onClick={() => setShowActions(false)}>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[150px] w-[min(680px,92vw)] rounded-2xl border bg-surface-raised p-3 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-caption uppercase tracking-wider font-semibold mb-2 text-text-dim">Sources</div>
            <div className="flex flex-wrap gap-1.5">
              {["Gmail", "Calendar", "Drive", "Limitless", "Square", "Contacts"].map((s) => (
                <span key={s} className="px-2 py-1 rounded-full text-caption font-medium bg-primary-muted text-primary border border-primary/20">@{s}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* OMNIPRESENT FLOATING CHAT BAR — also the universal trackpad. */}
      <div className="absolute bottom-0 left-0 right-0 z-40 px-3 pb-3 pointer-events-none flex justify-center">
        <div className="w-full max-w-3xl pointer-events-auto">
          {contextSource && (
            <div className="mb-2 px-3 py-2 bg-surface rounded-xl border border-primary/40 flex items-center justify-between shadow-lg">
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="w-1 h-4 rounded-full bg-primary shrink-0" />
                <span className="text-caption text-primary uppercase font-bold tracking-wider shrink-0">Talking about</span>
                <span className="text-body-sm text-text font-medium truncate">{contextSource.label}</span>
              </div>
              <button onClick={() => { contextSource.onDismiss?.(); onClearContext?.(); }} className="text-text-dim hover:text-text px-1 shrink-0"><X size={14} /></button>
            </div>
          )}

          {showSuggestions && (
            <div className="flex gap-2 pb-2 overflow-x-auto scrollbar-hide">
              {suggestions.map((s) => (
                <button key={s} onClick={() => onSuggestionClick?.(s)} className="shrink-0 px-3 py-1.5 rounded-full text-caption font-medium border bg-surface text-text-muted hover:text-text hover:border-primary/40 transition-colors">{s}</button>
              ))}
            </div>
          )}

          {/* Chat Card — the trackpad surface. touchAction:none lets us own the gesture. */}
          <div
            ref={barRef}
            data-testid="sage-chatbar"
            className="relative rounded-2xl border bg-surface shadow-2xl overflow-hidden transition-colors"
            style={{ borderColor: focused ? "var(--primary)" : undefined, touchAction: "pan-y" }}
            onTouchStart={barTouchStart}
            onTouchEnd={barTouchEnd}
            onMouseDown={barMouseDown}
            onMouseUp={barMouseUp}
          >
            <EdgeIndicator hCount={panels.length} hIndex={active} vIndex={vertical} hasAbove={!!panelAbove} hasBelow={!!panelBelow} />

            {/* modes row */}
            {onModeToggle && (
              <div className="flex items-center gap-1 px-2 pt-2">
                {([{ key: "code", label: "Code", icon: <CodeIcon size={11} /> }, { key: "write", label: "Write", icon: <PenLine size={11} /> }, { key: "research", label: "Research", icon: <Telescope size={11} /> }] as const).map((m) => {
                  const on = activeMode === m.key;
                  return (
                    <button key={m.key} onClick={() => onModeToggle(m.key)} className={`shrink-0 flex items-center gap-1 px-2 py-1 rounded-full text-caption font-semibold border transition-colors ${on ? "bg-primary-muted text-primary border-primary/40" : "border-transparent text-text-muted hover:text-text hover:bg-surface-hover"}`}>{m.icon}{m.label}</button>
                  );
                })}
              </div>
            )}
            {/* input row */}
            <div className="flex items-end gap-1.5 px-2 pt-2 pb-1">
              <button onClick={() => setShowActions((v) => !v)} className="shrink-0 p-2 rounded-full text-text-muted hover:text-text hover:bg-surface-hover transition-colors">{showActions ? <X size={18} /> : <Plus size={18} />}</button>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void handleSend(); } }}
                placeholder={`Ask SAGE about ${activeTitle.toLowerCase()}…`}
                rows={1}
                disabled={disabled}
                className="flex-1 bg-transparent text-text placeholder-text-dim outline-none text-body min-w-0 disabled:opacity-50 resize-none py-2 leading-tight max-h-28"
              />
              {hasText ? (
                <button onClick={() => void handleSend()} disabled={disabled} className="shrink-0 p-2 rounded-full bg-primary text-bg hover:bg-primary-hover disabled:opacity-40 transition-colors"><Send size={16} /></button>
              ) : (
                <button onClick={onMicClick} className="shrink-0 p-2 rounded-full text-text-muted hover:text-text hover:bg-surface-hover transition-colors"><Mic size={18} /></button>
              )}
            </div>
            {/* meta row */}
            <div className="flex items-center gap-1.5 px-3 pb-2 pt-1 border-t border-white/5">
              {activePersona && (
                <button onClick={onPersonaClick} className="flex items-center gap-1.5 px-2 py-1 rounded-full text-caption font-semibold transition-colors" style={{ backgroundColor: `color-mix(in srgb, ${activePersona.color} 18%, transparent)`, color: activePersona.color }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: activePersona.color }} />
                  {activePersona.shortName}
                </button>
              )}
              {onThinkingToggle && (
                <button onClick={onThinkingToggle} className="flex items-center gap-1 px-2 py-1 rounded-full text-caption font-semibold transition-colors" style={{ backgroundColor: thinkingEnabled ? "var(--primary-muted)" : "transparent", color: thinkingEnabled ? "var(--primary)" : "var(--text-muted)" }}>
                  <Brain size={11} />{thinkingEnabled ? "Thinking" : ""}
                </button>
              )}
              <div className="flex-1" />
              {modelBadge && (
                <button onClick={onModelClick} className="flex items-center gap-0.5 px-2 py-1 rounded-full text-caption font-semibold bg-primary-muted text-primary border border-primary/20 hover:bg-primary/20 transition-colors">{modelBadge}<ChevronDown size={10} /></button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SageShell;

function GridTile({ title, sub, icon, accentVar, active, onClick }: { title: string; sub: string; icon?: ReactNode; accentVar?: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`aspect-[4/3] rounded-xl border p-3 flex flex-col items-start text-left transition-colors ${active ? "border-primary/60 bg-primary-muted" : "border-border bg-surface hover:border-primary/30"}`}
      style={accentVar ? { borderLeftColor: `var(${accentVar})`, borderLeftWidth: 3 } : undefined}
    >
      <div className="flex items-center gap-2 text-caption uppercase tracking-wider text-text-dim font-bold mb-2">
        {icon}<span>{sub}</span>
      </div>
      <div className="text-body text-text font-semibold">{title}</div>
      <div className="mt-auto text-caption text-text-dim">{active ? "· active" : "tap to open"}</div>
    </button>
  );
}

/**
 * Four-edge position indicator on the chat card. Each edge holds a short segment
 * that slides along it: top/bottom track the horizontal (panel) position,
 * left/right track the vertical (mode) position. An edge hides in the direction
 * you moved AWAY from, so the surviving edges always point back toward center.
 */
function EdgeIndicator({ hCount, hIndex, vIndex, hasAbove, hasBelow }: { hCount: number; hIndex: number; vIndex: number; hasAbove: boolean; hasBelow: boolean }) {
  const hCenter = Math.floor((hCount - 1) / 2);
  const hDelta = hIndex - hCenter;
  const vMaxAbs = hasAbove || hasBelow ? 1 : 0;

  const H_SEG = 30, V_SEG = 42, H_INSET = 6, V_INSET = 8;
  const hAvail = Math.max(0, 100 - 2 * H_INSET - H_SEG);
  const vAvail = Math.max(0, 100 - 2 * V_INSET - V_SEG);
  const hPos = hCount > 1 ? hIndex / (hCount - 1) : 0.5;
  const vPos = vMaxAbs > 0 ? (vIndex + vMaxAbs) / (2 * vMaxAbs) : 0.5;
  const hLeft = H_INSET + hPos * hAvail;
  const vTop = V_INSET + vPos * vAvail;

  const showTop = vIndex <= 0, showBottom = vIndex >= 0;
  const showLeft = hDelta <= 0, showRight = hDelta >= 0;

  const accent = "var(--primary)";
  const THICK = 2;
  const ease = "cubic-bezier(0.22,1,0.36,1)";
  const baseH: React.CSSProperties = {
    position: "absolute", left: `${hLeft}%`, width: `${H_SEG}%`, height: THICK,
    backgroundColor: accent, borderRadius: 999, pointerEvents: "none",
    transition: `left 320ms ${ease}, opacity 200ms ease-out`,
  };
  const baseV: React.CSSProperties = {
    position: "absolute", top: `${vTop}%`, height: `${V_SEG}%`, width: THICK,
    backgroundColor: accent, borderRadius: 999, pointerEvents: "none",
    transition: `top 320ms ${ease}, opacity 200ms ease-out`,
  };
  return (
    <>
      {showTop && <div style={{ ...baseH, top: 0 }} />}
      {showBottom && <div style={{ ...baseH, bottom: 0 }} />}
      {(hasAbove || hasBelow) && showLeft && <div style={{ ...baseV, left: 0 }} />}
      {(hasAbove || hasBelow) && showRight && <div style={{ ...baseV, right: 0 }} />}
    </>
  );
}
